/**
 * Shared column-width tokens for `CustomDataTable` (mantine-datatable) columns.
 *
 * The sizing model is "atoms fixed, primary flexes": every non-text "atom"
 * column (status, badge, date, number, progress, avatar, code/id, actions)
 * gets a tight fixed width matched to what it renders, and exactly one primary
 * text column per table carries NO width so it flexes to absorb remaining
 * space. Omit `ellipsis` on that column so long values wrap instead of forcing
 * a horizontal scroll; some tables set `ellipsis: true` to truncate to one line.
 *
 * Widths are tuned to the rendered content, not the data: an avatar-only
 * "Owner" cell needs ~64px regardless of the person's name length (names show
 * in a tooltip). Where a column's header text/tooltip is wider than its cell
 * content, use the `*Wide`/`*WithHint` variant so the header is not clipped.
 */
export const COLUMN_WIDTH = {
  /** Secondary/long text: description, justification, email, department, mapping. */
  longText: 240,
  /** Short text/badge column (source, severity word, short status). */
  shortText: 120,
  /** Status rendered as colored text. */
  status: 120,
  /** Status text whose header is wider than the value (CIA triad, Treatment Decision, Visibility). */
  statusWide: 130,
  /** Badge/pill with a short label (Yes/No, severity word, Active). */
  badgeSm: 100,
  /** Badge/pill with a medium label (Uncalculated, In Progress, framework, risk level, trend, SLA). */
  badgeMd: 130,
  /** Wide badge/pill — dual-tone badges (label + value) or longer single labels. */
  badgeWide: 170,
  /** Single date or relative time ("12 Jun 2025", "3 days ago"). */
  date: 120,
  /** Two-date range (e.g. an audit Period). */
  dateRange: 200,
  /** Absolute datetime, or a two-line relative time with a subtitle. */
  dateTime: 160,
  /** Number, count, ratio, or formatted hours. */
  number: 90,
  /** Progress bar + percentage. */
  progress: 150,
  /** Progress column whose header carries a tooltip/info icon (e.g. Coverage). */
  progressWithHint: 160,
  /** Single avatar, avatar-only (Owner/Incharge). */
  avatar: 64,
  /** Multiple avatars, avatar-only (Assignees/Attendees/Authors). */
  avatarGroup: 100,
  /** Avatar plus an inline name text. */
  avatarName: 180,
  /** Short identifier/code (criteria, MITRE id, incident id, version, region). */
  code: 120,
  /** Longer identifier (cloud account id / Azure subscription name). */
  codeLong: 160,
  /** Icon-only action column (caret / kebab menu). */
  iconAction: 64,
  /** Labeled action button (View Report, Remove, Unmap, Revoke). */
  action: 100,
  /** Action button with a long label (e.g. "Mark as Applicable"). */
  actionWide: 160,
} as const;

export type ColumnWidthToken = keyof typeof COLUMN_WIDTH;
